// janus-engine/actions/weapon-attack-extra-damage.ts — A2 S10: the rest of the damage pipeline (PURE).
//
// Until S10 the resolver rolled the weapon's own fragment and stopped, while legacy runs the whole pipeline:
//  · damage_type_rolls.php fans out EXTRA fragments — the attacker's extra-damage passives (triggered on the melee attack
//    roll, or untriggered), untriggered extra-weapon-damage passives, the weapon's own attached ones, and the TARGET's
//    extra-damage-taken passives. Each is a full damage_type_roll(): its own dice, damage type, explosions, crit bonus and
//    mitigation. This is where a MIXED-TYPE damage row comes from (a Fire weapon + a Cold rider).
//  · calc_total_damage_and_apply_to_target.php then sums them and applies the UNTYPED adjusters in order: the attacker's
//    output percentage, the target's incoming percentage, Damage Mitigation, reduce-damage statuses, the Shielded stack,
//    the warding damage transfer — and writes roll_feedback (rolled / applied per damage type).
// Census 2026-09-14 (tools/probes/_a2_s10_damage_pipeline_census.php): 144 creatures host extra-damage passives directly,
// 208 author Damage Mitigation, ~5,200 fragment items — almost all plain `NdN`. So this is not an edge case: before S10
// the lane silently dealt less damage than legacy for every one of them.
//
// What is RESOLVED here and what still HANDS BACK (pre-draw, on state — never after a die is rolled):
//   resolved   caster passive extra_damage_roll (triggered on the melee attack roll: hit / crit / at-adv gates; untriggered),
//              untriggered extra_weapon_damage_roll (caster passives + the weapon's attached entries, one roll per id),
//              the attacker's status-applied extra-damage passives (ft_status_item_passive_entries: dedup, team, conditions — 2026-10-16),
//              triggered extra_weapon_damage_roll (on hit / on crit), the target's extra_damage_roll_taken (marked-by-caster
//              gate, one roll per ability + granter), per-fragment explosions / crit / mitigation, both percentages,
//              Damage Mitigation, literal reduce-damage statuses, roll_feedback, and a fragment's FAILING creature-type / target-status
//              post-condition (rolled, then zeroed with PHP's δ🛡️ / σ− tuple — Dirty Performer, 2026-10-16), damage_heals_caster.
//   inert      a status-applied Last Stand ward (Death Ward Effect) on either side — the settle frame fires it at zero, never the planner.
//   hand back  any other status-applied passive row outside the already-compiled layer set, fragments carrying
//              any layer outside FRAGMENT_KNOWN_LAYERS, tag dice / mods,
//              damage-transfer statuses on the target, the reroll providers.
import type { RollSource } from './roll-source.js';
import { ecLean } from '../ability/context.js';
import { isAttackPassiveWindow } from '../compile/status-attack-budget.js';
import { rowLayerNames } from '../compile/row-layer-names.js';
import { applyDamagePercentage } from '../ability/rolls/damage-percentage.js';
import { criticalHitBonus, explosionChain, type CritVariant } from './weapon-attack-dice.js';
import { EXTRA_DAMAGE_REQUIRES_DAMAGE_TYPE, extraDamageTypeGateAllows } from '../ability/rolls/extra-damage-requires-damage-type.js';
import { DAMAGE_PROVIDER_LAYERS, damageProviderDefinitions, damageProviderFlag, ignoredMitigationTypes, mitigationAfterTypedIgnore } from '../ability/rolls/ignore-damage-mitigation-damage-type.js';
import { ATTACKS_IGNORE_NATURAL_DAMAGE_MITIGATION, mitigationAfterNaturalIgnore, naturalDamageMitigation } from '../ability/rolls/ignore-natural-damage-mitigation.js';
import { isAbilityConditionLayer } from '../compile/ability-conditions.js';
import { damageReductionPlan, rollDamageReductions, type DamageReduction } from '../ability/rolls/reduce-damage-status.js';
export { REDUCE_DAMAGE_ROLL } from '../ability/rolls/reduce-damage-status.js';
import { resolveQuantityAlias } from '../reactions/trigger-dispatch.js';
import { statusItemPassiveEntries, type StatusItemPassiveReaders } from '../ability/rolls/status-item-passive-entries.js';
import { FRAGMENT_KNOWN_LAYERS, isStatusPassiveExtraDamageLayer } from '../ability/rolls/extra-damage-layers.js';
export { FRAGMENT_KNOWN_LAYERS } from '../ability/rolls/extra-damage-layers.js';
import { LAST_STAND_ROLL_TYPES } from '../death/last-stand-roll-types.js';

type Row = Record<string, unknown>;

export const EXTRA_DAMAGE_ROLL = '9878714A-27C3-D737-88B3-E8C1EE63A20F';
export const EXTRA_WEAPON_DAMAGE_ROLL = '42A38751-5549-CDE7-528E-AED309A2B07D';
export const EXTRA_DAMAGE_ROLL_TAKEN = '99DC3B29-702D-08D0-346E-AA4359EA1D82';
const DAMAGE_TYPE_BLUDGEONING = '0C528CF8-1B5A-453C-9BDE-73CBBC239A2A';
const UUID = /^[0-9A-Fa-f]{8}(-[0-9A-Fa-f]{4}){3}-[0-9A-Fa-f]{12}$/;
/** Already-folded skill/stat contributions and authoring metadata need no damage-time evaluation.
 * Conditions, nested grants and event listeners require their own evaluated carrier, even without an extra-damage roll. */
const COMPILED_STATUS_PASSIVE_LAYERS = new Set([
  'skill_roll_adv', 'skill_roll_dis', 'skill_roll_mod', 'skill_rolls_adv', 'skill_rolls_dis', 'skill_rolls_mod',
  'speed_walking', 'speed_flying', 'speed_swimming', 'speed_climbing', 'speed_burrowing',
  // the rest of the compiled speed families (passive-integer-fold.ts CPI_SPEED_FAMILIES → computed.creature.speeds): "Fixed Teleport Speed: 120"
  'speed_hovering', 'teleport',
  'requires_class', 'requires_class_level', 'requires_subclass', 'requires_level',
  'cost_gp', 'unenchantable', 'duration_int', 'duration_type', 'range',
  // Integer/status defense compiles own these totals. Attack threshold, AC, size,
  // contesting/status immunity and fragment/total defense readers consume them.
  'critical_hit_range_increase_mod', 'ac_mod_magical', 'ac_mod_non_magical', 'size',
  'immune_status_effect', 'contesting_roll_adv_vs_status_effect', 'contesting_roll_dis_vs_status_effect',
  'damage_type_resistance', 'damage_type_immunity', 'damage_type_vulnerability', 'damage_mitigation',
  // The swing's statusRollSources and the ability skill-roll reader evaluate these
  // on their due roll; no new fragment or event listener is implied.
  'roll_type_adv', 'roll_type_dis',
  // Senses fold into the holder's compiled senses (compile/creature-senses-compile.ts FT_SENSE_NAMES reads status grants); a swing's
  // arithmetic never reads them. Listed inline, like integer-compute-slots.ts.
  'nightsight', 'fiendsight', 'blindsight', 'ghostsight', 'spellsight', 'truesight', 'tremorsense', 'telepathy', 'windsense',
]);

/** A Last Stand ward's own layers: the self-zero trigger and the vitality roll it fires (Death Ward Effect — owner playtest 2026-10-21). */
const LAST_STAND_WARD_LAYERS: ReadonlySet<string> = new Set(['triggers_on_self_zero_vitality', 'roll_type', 'dice', 'die', 'mod', 'ability_charges']);

/** Shared by held-status damage planning and on-hit status admission. */
export function statusPassiveDamageSupported(status: Row, definition: Row | null,
  read: Pick<ExtraDamageReaders, 'value' | 'entries'>): boolean {
  return unsupportedStatusPassiveLayers(definition, read).length === 0;
}

/** The admission gate's exact residual layers, also used by catalog diagnostics. */
export function unsupportedStatusPassiveLayers(definition: Row | null,
  read: Pick<ExtraDamageReaders, 'value' | 'entries'>): string[] {
  if (!definition) return ['definition-not-held'];
  if (isAttackPassiveWindow(definition)) return [];
  const rollType = upper(scalar(read.value(definition, 'roll_type')));
  const fragmentSource = [EXTRA_DAMAGE_ROLL, EXTRA_WEAPON_DAMAGE_ROLL].includes(rollType);
  const damageProvider = [...DAMAGE_PROVIDER_LAYERS].some(key => nonEmpty(read.value(definition, key)));
  // The swing never reads a Last Stand ward: its shared settle frame fires it at zero (resolvePlayerDying → tryLastStand), before any death.
  const lastStandWard = nonEmpty(read.value(definition, 'triggers_on_self_zero_vitality')) && LAST_STAND_ROLL_TYPES.includes(rollType);
  return layerNamesOf(definition).filter(layer => {
    // Weapon fragments currently roll one face per die. Keep damage-roll modifiers named-refused
    // until that lane can preserve discarded faces, rather than silently admitting their payload.
    if (['roll_type_adv', 'roll_type_dis'].includes(layer) && read.entries(definition, layer)
      .some(entry => ['82BBB3B6-4646-48E8-A5A2-8DC0947A91D0', EXTRA_DAMAGE_ROLL, EXTRA_WEAPON_DAMAGE_ROLL].includes(layerListEntryUuid(entry)))) return true;
    return !(COMPILED_STATUS_PASSIVE_LAYERS.has(layer)
      || damageProvider && (DAMAGE_PROVIDER_LAYERS.has(layer) || isAbilityConditionLayer(layer)
        || ['aoe_type', 'aoe_distance', 'persistent_aoe', 'aoe_follows_target', 'effect_only_affects_opposing_team_type',
          'effect_only_affects_same_team_type', 'if_aura_owner_has_status_effect_active', 'triggers_after_combat_begins'].includes(layer))
      || fragmentSource && isStatusPassiveExtraDamageLayer(layer)
      || lastStandWard && LAST_STAND_WARD_LAYERS.has(layer));
  });
}

/** Forced-max-vs-creature-type provider layer (max_damage_vs_creature_type.php, 2026-09-14) — unported. */
const MAX_DAMAGE_VS_CREATURE_TYPE_LAYER = 'maximum_damage_against_creature_type';

/** Layers on the attacker (root / passive / feat) that re-roll or convert EVERY fragment's dice — unported. */
const REROLL_PROVIDER_LAYERS = ['damage_type_reroll_1s_and_2s', 'damage_type_roll_1_becomes_2'] as const;
/** Weapon-fragment post-conditions (check_post_damage_type_roll_conditions reads them off the weapon too) — unported. */
const WEAPON_POST_CONDITION_LAYERS = [
  'contesting_roll_successful_for_half_damage', 'ability_only_works_against_size_category_number_above', 'ability_only_works_against_size_category_number_below',
  'ability_works_only_against_creature_type', 'ability_does_not_work_against_creature_type', 'ability_works_only_against_creature_sub_type',
  'ability_does_not_work_against_creature_sub_type', 'ability_works_only_against_creature_lineage', 'ability_works_only_against_creature_ancestry',
  'ability_works_only_against_creature_grouping', 'ability_does_not_work_against_creature_lineage', 'ability_does_not_work_against_creature_ancestry',
  'ability_does_not_work_against_creature_grouping', 'if_target_has_status_effect_active', 'if_target_has_status_effect_not_active',
  'damage_dice_quantity_mod', 'versatile_dice',
] as const;

export type MitigationKind = 'resistance' | 'immunity' | 'vulnerability';
export interface FragmentMitigationStep { kind: MitigationKind; layer: string; meta: Record<string, unknown>; ignored?: true }

export interface ExtraDamageReaders {
  value: (row: Row, name: string) => unknown;
  entries: (row: Row, name: string) => unknown[];
  /** A catalog definition as seen from THIS row (its own catalog first). */
  definitionOn: (row: Row, id: string) => Row | null;
  /** The row's active status rows (`statuses.target`). */
  statusRows: (row: Row) => Row[];
  statusItemIds: (row: Row) => string[];
  hasStatus: (row: Row, name: string) => boolean;
  /** The encounter roster row of a creature id (a status's applier — buff_does_not_layer_onto_opposing_team); undefined = not held. */
  member?: (id: string) => Row | undefined;
  conditionScene?: StatusItemPassiveReaders['conditionScene'];
}

/** One extra fragment the attack WILL roll if its outcome gate passes — everything settled on state, before any draw. */
export interface ExtraDamagePlan {
  /** Which loop of damage_type_rolls() produced it (the order they roll in). */
  bucket: 'caster' | 'weapon' | 'target';
  /** 'always' rolls on every hit; 'crit' only on a critical hit. */
  when: 'always' | 'crit';
  /** triggers_if_roll_type_is_at_adv: only when the attack roll resolved at net advantage. */
  requiresAdvantage: boolean;
  sourceId: string;
  /** A target extra-damage-taken grant's once-per-ACTION key (ability id | granting status caster); absent = rolls on every strike. */
  grantKey?: string;
  sourceName: string;
  sourceItemType: string;
  dice: number;
  die: number;
  mod: number;
  damageTypeId: string;
  /** This fragment's defended amount contributes to the shared damageHealsCaster restore. */
  healsCaster?: boolean;
  mitigation: FragmentMitigationStep[];
  /** build_damage_dice_meta's base for this fragment, in PHP's key order. */
  metaBase: Record<string, unknown>;
  /** Wizard v9 damage_type_is_rolled_at_max: the attacker's provider name when this fragment's type is rolled at max (set by the resolver). */
  maxedBy?: string | null;
  /** check_post_damage_type_roll_conditions gates this fragment FAILS, in PHP's order: it still rolls (dice, explosions, crit,
   *  mitigation) and then contributes 0, its tuples stamped not-applied and each gate's explanation tuple appended. */
  gates?: ExtraDamageGate[];
}

/** One failing post-condition gate: the reason code that stamps the fragment's tuples and the explanation tuple PHP appends. */
export interface ExtraDamageGate { reason: string; face: string; label: string; meta: Record<string, unknown> }

/** One rolled extra fragment. */
export interface ExtraDamageFragment extends ExtraDamagePlan {
  draws: number[];
  explosions: Array<{ value: number; link: number; threshold: number }>;
  criticalVariant: CritVariant | null;
  criticalBonus: number;
  criticalTuples: Array<{ value: number; label: string; kind: 'critical' | 'explosion' }>;
  /** dice + mod + explosions + crit — PHP's `feedback_rolled` (before the fragment floor). */
  beforeMitigation: number;
  /** After mitigation, floored at 0 — what the fragment adds to the total. */
  result: number;
  /** 'immune' / 'fully resisted' when mitigation zeroed it (feedback_zero_reason). */
  zeroReason: string | null;
}

/** calc_total_damage_and_apply_to_target's untyped adjusters that fired, as log tuples in PHP's order. */
export interface DamageAdjustment { face: string; label: string }

export interface DamageFeedbackType { damage_type_id: string; rolled: number; applied: number | null; typed: number; reason: string | null }
export interface DamageFeedback { rolled: number; applied: number; verb: 'taken'; reason: string | null; damage_types?: DamageFeedbackType[] }

export type ExtraDamageScope = { supported: true; plans: ExtraDamagePlan[]; outputPercent: number; incomingPercent: number; damageMitigation: number;
  reductions?: DamageReduction[];
  /** Missing held status definitions, reported only when a swing reaches damage mitigation. */
  skippedEffects?: string[];
  /** Shared provider answer; preserved when the caller supplies its direct weapon flag. */
  providerMitigationIgnored?: boolean;
  /** ignore_damage_mitigation_damage_type (Molten Impact — R-147): upper-cased damage type ids whose fragments Damage Mitigation skips. */
  mitigationIgnoredTypes?: string[];
  /** attacks_ignore_natural_damage_mitigation (Living Arsenal — R-180): the target's natural share of damageMitigation this hit skips. */
    naturalMitigation?: number } | { supported: false; reason: string; sourceName?: string };

const record = (value: unknown): Row | null => value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Row : null;
const scalar = (value: unknown): unknown => { let v = value; while (Array.isArray(v)) v = v[0]; return v; };
const upper = (value: unknown): string => String(value ?? '').trim().toUpperCase();
const itemField = (row: Row | null, key: string): unknown => scalar(record(row?.items)?.[key] ?? row?.[key]);
/** layer_list_entry_uuid twin: item_layer_effect → the bucketed shell's items.item_id → a flat item_id → the string itself, each cut at
 *  a `|`. (Codex S10 review F1: reading only `item_id` missed `{items:{item_id}}` shells and silently skipped triggered fragments.) */
export function layerListEntryUuid(entry: unknown): string {
  const cut = (value: unknown): string => (typeof value === 'string' && value !== '' ? value.split('|')[0]! : '');
  const row = record(entry);
  if (row) return upper(cut(row.item_layer_effect) || cut(record(row.items)?.item_id) || cut(row.item_id));
  return upper(cut(entry));
}
const entryUuid = layerListEntryUuid;
/** v_status_id_of(): the row's vitality tier — a UUID or an item shell on computed.vitality; null when the row carries none. */
const vitalityTierOf = (row: Row): string | null => {
  const vitality = record(record(row.computed)?.vitality);
  if (!vitality || !('v_status' in vitality)) return null;
  const raw = vitality.v_status;
  const id = typeof raw === 'string' ? raw : itemField(record(raw), 'item_id');
  return typeof id === 'string' && id.trim() !== '' ? upper(id) : null;
};
const hasLayers = (row: Row | null): boolean => { const layers = record(row?.item_layer_types); return !!layers && Object.keys(layers).length > 0; };
/** Bucketed and legacy flat layers share one reader (row-layer-names.ts, also the family and budget gates' enumeration).
 * Only row structure and `_`-prefixed object-API metadata are excluded; unknown root keys remain unsupported regardless of value shape. */
const layerNamesOf = rowLayerNames;
/** PHP empty() on a scalar-read layer. */
const nonEmpty = (value: unknown): boolean => { const v = scalar(value); return v !== undefined && v !== null && v !== '' && v !== 0 && v !== '0' && v !== false; };
/** contains_trigger_key twin: a key containing "trigger" at the top level OR in the layer bucket. */
export function containsTriggerKey(def: Row): boolean {
  return [...Object.keys(def), ...Object.keys(record(def.item_layer_types) ?? {})].some(key => key.includes('trigger'));
}
/** passive_ability_entry_is_active twin: key absent → active; any falsy stamp → inactive. */
function entryActive(entry: Row): boolean {
  if (!('passive_conditions_active' in entry)) return true;
  return Boolean(scalar(entry.passive_conditions_active)) && scalar(entry.passive_conditions_active) !== '0';
}
/** A stored integer (PHP `is_numeric` on the scalar). Null when absent; NaN when present but not a plain number (a `[body]` tag). */
function intLayer(read: ExtraDamageReaders, def: Row, name: string): number | null {
  const raw = scalar(read.value(def, name));
  if (raw === undefined || raw === null || raw === '') return null;
  const n = typeof raw === 'number' ? raw : (typeof raw === 'string' && /^\s*[+-]?\d+\s*$/.test(raw) ? Number(raw) : NaN);
  return Number.isInteger(n) ? n : NaN;
}
/** derive_damage_source_kind twin — the whole switch (Codex S10 review F5: feats log as 'class', lineage as 'ancestry'). */
export function sourceKindOf(itemType: string): string | null {
  switch (itemType) {
    case 'weapon': case 'natural_weapon': return 'weapon';
    case 'passive_ability': return 'passive_ability';
    case 'status_effect': return 'status_effect';
    case 'attack_ability': case 'opportunity_attack_ability': case 'ability': case 'full_ability': case 'quick_ability': case 'free_ability': case 'reaction_ability': return 'ability';
    case 'spell': case 'cantrip': return 'spell';
    case 'creature_type': case 'creature_sub_type': case 'creature_sub_type_ancestral': case 'creature_sub_type_grouping': case 'creature_ancestry': case 'creature_grouping': case 'creature_lineage': return 'creature_type';
    case 'ancestry': case 'lineage': return 'ancestry';
    case 'class': return 'class';
    case 'subclass': return 'subclass';
    case 'background': return 'background';
    case 'feat': return 'class';
    case '': return null;
    default: return itemType;
  }
}

const ABILITY_BUCKETS = ['passive_ability', 'full_ability', 'ability', 'quick_ability', 'attack_ability', 'free_ability', 'reaction_ability', 'opportunity_attack_ability'] as const;
/**
 * find_caster_layer_provider_arr twin (presence only): the root / classification carriers, every ability bucket entry (a stamped-false
 * passive skipped), each feat's OWN layers, then the abilities a feat grants (one level). Codex S10 review F2: the first cut read only the
 * passive bucket and the feat itself. A feat or grant whose definition the browser does not hold cannot be judged — a recorded residual.
 */
function casterProvides(read: ExtraDamageReaders, caster: Row, layer: string): boolean {
  if (nonEmpty(read.value(caster, layer))) return true;
  const resolve = (entry: unknown): Row | null => {
    const unwrapped = record(record(entry)?.item_arr) ?? entry;
    const row = record(unwrapped);
    if (hasLayers(row)) return row;
    const id = layerListEntryUuid(unwrapped);
    return UUID.test(id) ? read.definitionOn(caster, id) : null;
  };
  for (const bucket of ABILITY_BUCKETS) {
    for (const entry of read.entries(caster, bucket)) {
      const row = record(entry);
      if (!row || row.passive_conditions_active === false) continue;
      const ability = record(row.item_arr) ?? row;
      if (nonEmpty(read.value(ability, layer))) return true;
    }
  }
  for (const featEntry of read.entries(caster, 'feat')) {
    const feat = resolve(featEntry);
    if (!feat) continue;
    if (nonEmpty(read.value(feat, layer))) return true;
    for (const bucket of ABILITY_BUCKETS) {
      for (const granted of read.entries(feat, bucket)) {
        const def = resolve(granted);
        if (def && nonEmpty(read.value(def, layer))) return true;
      }
    }
  }
  return false;
}

/**
 * Plan every extra fragment + read the untyped adjusters, or hand the attack back. Pure state — no draws.
 * `mitigationFor` is the resolver's own first-wins defence matcher (shared with the weapon fragment).
 */
export function planExtraDamage(input: {
  attacker: Row; target: Row; weapon: Row; attackerId: string; meleeRollType: string;
  read: ExtraDamageReaders;
  mitigationFor: (damageTypeId: string, fragment: Row) => FragmentMitigationStep[] | { reason: string };
  vitalityStatusIds: ReadonlySet<string>;
}): ExtraDamageScope {
  const { attacker, target, weapon, read } = input;
  const no = (reason: string, source?: Row): ExtraDamageScope => ({ supported: false, reason,
    ...(source ? { sourceName: String(itemField(source, 'item_name') ?? itemField(source, 'item_id') ?? 'unnamed ability') } : {}) });

  // ---- pipeline pieces this slice does not port: hand back before any draw ----
  for (const layer of WEAPON_POST_CONDITION_LAYERS) {
    // damage_type_roll changes versatile dice only for a two-handed melee use.
    if (layer === 'versatile_dice' && (Number(read.value(attacker, 'count_both_hands_weapon_equipped') ?? 0) !== 1
        || upper(read.value(weapon, 'roll_type')) !== '2FE8DFB7-FE61-4862-AF71-3523262350A0')) continue;
    if (read.entries(weapon, layer).length > 0) return no('weapon-fragment-condition-out-of-scope');
  }
  const weaponMod = intLayer(read, weapon, 'mod');
  if (weaponMod !== null && weaponMod !== 0) return no('weapon-damage-modifier-out-of-scope'); // damage_type_roll reads `mod`
  // The reroll providers re-roll or convert EVERY fragment's dice (the weapon's included) wherever PHP's provider walk finds them.
  for (const layer of REROLL_PROVIDER_LAYERS) if (casterProvides(read, attacker, layer)) return no('damage-reroll-provider-out-of-scope');
  // Target statuses that change the total after the fragments: reduce-damage rolls, the warding transfer, Shielded.
  // Unheld status definitions stay outside this walk. The shared attacker mitigation walk below records its due skips.
  const present = (def: Row, name: string): boolean => { const v = read.value(def, name); return v !== undefined && v !== null && v !== ''; };
  const reductions: NonNullable<Extract<ExtraDamageScope, { supported: true }>['reductions']> = [];
  for (const [row, side] of [[attacker, 'attacker'], [target, 'target']] as const) {
    for (const status of read.statusRows(row)) {
      const embedded = record(status.status_arr);
      const def = hasLayers(embedded) ? embedded : read.definitionOn(row, upper(scalar(status.status_item_id) ?? itemField(embedded, 'item_id')));
      const itemType = String(itemField(def, 'item_type') ?? itemField(embedded, 'item_type') ?? '');
      // A status-applied extra-damage passive: on the ATTACKER a fragment source (ft_status_item_passive_entries, planned below); on the
      // TARGET inert — PHP's target loop acts only on extra_damage_roll_taken (still out of scope here: not admitted).
      if (itemType === 'passive_ability' && !statusPassiveDamageSupported(status, def, read))
        return no(`${side}-status-passive-out-of-scope`, def ?? undefined);
      if (side === 'attacker' || !def) continue;
      if (present(def, 'transfer_damage_percentage_to_caster_of_status_effect')) return no('damage-transfer-status-out-of-scope');
      const reduction = damageReductionPlan(def, read.value);
      if (reduction === 'invalid') return no('reduce-damage-status-dice-out-of-scope');
      if (reduction) reductions.push(reduction);
    }
  }

  // ---- the extra fragments, in damage_type_rolls() order ----
  const plans: ExtraDamagePlan[] = [];
  // Only these definitions have passed the shared status condition / aura-owner gates. Other fragment sources keep their own scope.
  const checkedStatusPassives = new Set<Row>();
  const rolledWeaponExtra = new Set<string>();
  const planFragment = (def: Row, bucket: ExtraDamagePlan['bucket'], when: ExtraDamagePlan['when'], requiresAdvantage: boolean): string | null => {
    const unknown = layerNamesOf(def).find(layer => !FRAGMENT_KNOWN_LAYERS.has(layer)
      && !(checkedStatusPassives.has(def) && isStatusPassiveExtraDamageLayer(layer)));
    if (unknown) return `extra-damage-layer-out-of-scope:${unknown}`;
    const sourceOrigin = scalar(def.source);
    if (typeof sourceOrigin === 'string' && UUID.test(sourceOrigin)) return 'extra-damage-origin-out-of-scope';
    const dice = intLayer(read, def, 'dice'); const die = intLayer(read, def, 'die'); const mod = intLayer(read, def, 'mod') ?? 0;
    if (dice === null || die === null || !Number.isInteger(dice) || !Number.isInteger(die)
      || dice < 0 || die < 0 || (dice > 0 && die < 1) || dice > 100 || die > 1000) return 'extra-damage-dice-out-of-scope';
    if (!Number.isInteger(mod)) return 'extra-damage-modifier-tag-out-of-scope';
    // damage_type_roll: the fragment's own damage_type, else the caster's main weapon type, else Bludgeoning. RAW, like the weapon's.
    const ownType = String(scalar(read.value(def, 'damage_type')) ?? '').trim();
    const mainType = String(scalar(read.value(attacker, 'main_weapon_damage_type')) ?? '').trim();
    const damageTypeId = ownType || mainType || DAMAGE_TYPE_BLUDGEONING;
    if (!UUID.test(damageTypeId)) return 'extra-damage-type-unreadable';
    const mitigation = input.mitigationFor(damageTypeId, def);
    if (!Array.isArray(mitigation)) return mitigation.reason;
    // Post-conditions (check_post_damage_type_roll_conditions), settled on STATE (creature type, active statuses): a PASSING gate adds
    // nothing; a FAILING one still rolls the fragment and zeroes it with an explanation tuple (rollExtraFragment / extraFragmentRollsFull).
    // PHP order: creature type only → creature type excluded → target status required. Dirty Performer (2026-10-16) is the status case.
    const gates: ExtraDamageGate[] = [];
    const gate = (reason: string, face: string, label: string, ids: string[], statusIds = false): void => {
      const meta: Record<string, unknown> = { ft_gate_explanation: true, ft_gate_reason_code: reason };
      if (ids.length) { if (statusIds) meta.ft_gate_status_item_ids = ids; meta.ft_gate_icon_item_ids = ids; }
      gates.push({ reason, face, label, meta });
    };
    const distinctIds = (layer: string): string[] => [...new Set(read.entries(def, layer).map(entryUuid).filter(id => UUID.test(id)))];
    const targetType = entryUuid(read.value(target, 'creature_type'));
    const only = read.entries(def, 'ability_works_only_against_creature_type').map(entryUuid).filter(Boolean);
    if (read.entries(def, 'ability_works_only_against_creature_type').length > 0 && !(targetType !== '' && only.includes(targetType)))
      gate('creature_type_only_miss', 'δ🛡️', 'Creature Type Immune', distinctIds('ability_works_only_against_creature_type'));
    const excluded = read.entries(def, 'ability_does_not_work_against_creature_type').map(entryUuid).filter(Boolean);
    if (targetType !== '' && excluded.includes(targetType))
      gate('creature_type_excluded', 'δ🛡️', 'Creature Type Immune', UUID.test(targetType) ? [targetType] : []);
    const required = read.entries(def, 'if_target_has_status_effect_active').map(entryUuid).filter(Boolean);
    if (read.entries(def, 'if_target_has_status_effect_active').length > 0) {
      // ft_target_has_status: a vitality tier (Bloodied …) matches computed.vitality.v_status (a row without one holds no tier), any id
      // matches a held status row through has_status_effect's quantity alias (Bleeding (2d6) is stored as its base row)
      const held = read.statusItemIds(target).map(upper);
      const tier = vitalityTierOf(target);
      const holds = (id: string): boolean => held.includes(resolveQuantityAlias(id)) || (input.vitalityStatusIds.has(id) && tier === id);
      if (!required.some(holds))
        gate('status_required_missing', 'σ−', 'Target Does Not Have Status Effect Active', distinctIds('if_target_has_status_effect_active'), true);
    }
    const sourceId = String(itemField(def, 'item_id') ?? '');
    const sourceName = String(itemField(def, 'item_name') ?? '');
    const sourceItemType = String(itemField(def, 'item_type') ?? '');
    const metaBase: Record<string, unknown> = { damage_type_id: damageTypeId };
    if (sourceId !== '') metaBase.source_item_id = sourceId;
    if (sourceName !== '') metaBase.source_name = sourceName;
    if (sourceItemType !== '') metaBase.source_item_type = sourceItemType;
    const kind = sourceKindOf(sourceItemType);
    if (kind) metaBase.source_kind = kind;
    plans.push({ bucket, when, requiresAdvantage, sourceId, sourceName, sourceItemType, dice, die, mod, damageTypeId, mitigation, metaBase,
      ...(read.entries(def, 'damage_heals_caster').length ? { healsCaster: true } : {}), ...(gates.length ? { gates } : {}) });
    return null;
  };
  /** damage_type_rolls' $roll_one_extra_weapon_ability: untriggered extra_weapon_damage_roll, one roll per catalog id. */
  const rollOneExtraWeapon = (def: Row, bucket: ExtraDamagePlan['bucket']): string | null => {
    const id = upper(itemField(def, 'item_id'));
    if (UUID.test(id) && rolledWeaponExtra.has(id)) return null;
    if (upper(scalar(read.value(def, 'roll_type'))) !== EXTRA_WEAPON_DAMAGE_ROLL || containsTriggerKey(def)) return null;
    const why = planFragment(def, bucket, 'always', false);
    if (why) return why;
    if (UUID.test(id)) rolledWeaponExtra.add(id);
    return null;
  };
  const resolveEntryDef = (row: Row, entry: Row): Row | null | 'none' => {
    const nested = entry.item_arr ?? entry.item_id;
    const full = record(nested);
    if (hasLayers(full)) return full;
    if (nested === undefined || nested === null) return 'none';
    const id = upper(typeof nested === 'string' ? nested : itemField(full, 'item_id'));
    if (!UUID.test(id)) return 'none';
    return read.definitionOn(row, id);
  };
  const triggerListed = (def: Row): boolean => read.entries(def, 'triggers_when_caster_makes_roll_type').map(entryUuid).includes(upper(input.meleeRollType));
  const flag = (def: Row, name: string): boolean => { const v = scalar(read.value(def, name)); return v !== undefined && v !== null && v !== '' && v !== 0 && v !== '0' && v !== false; };

  // extra_damage_requires_damage_type (Overheat, 2026-10-07; PHP ft_extra_damage_damage_type_gate_allows — R-146): an extra_damage_roll
  // rider listing damage types fires only when the WEAPON deals one — its own damage_type, else the attacker's main_weapon_damage_type.
  const weaponDamageTypes = ((): string[] => {
    const own = read.entries(weapon, 'damage_type').map(entryUuid).filter(Boolean);
    if (own.length) return own;
    const main = upper(scalar(read.value(attacker, 'main_weapon_damage_type')));
    return main ? [main] : [];
  })();
  const extraTypeGate = (def: Row): boolean => extraDamageTypeGateAllows(read.entries(def, EXTRA_DAMAGE_REQUIRES_DAMAGE_TYPE).map(entryUuid), weaponDamageTypes);
  // One caster passive entry, damage_type_rolls' caster loop body: null = planned or nothing to roll.
  const casterPassive = (def: Row): ExtraDamageScope | null => {
    // A weapon-bound max-damage provider (max_damage_vs_creature_type.php — e.g. an enchantment part bucketed with
    // `source` = the equipped row) forces the weapon's fragments to max vs a creature type. Unported → hand back.
    if (read.entries(def, MAX_DAMAGE_VS_CREATURE_TYPE_LAYER).length > 0) return no('max-damage-vs-creature-type-out-of-scope');
    const rt = upper(scalar(read.value(def, 'roll_type')));
    const triggered = containsTriggerKey(def);
    if (triggered && rt === EXTRA_DAMAGE_ROLL && triggerListed(def) && extraTypeGate(def)) {
      const onHit = flag(def, 'triggers_on_hit'), onCrit = flag(def, 'triggers_on_critical_hit');
      const why = planFragment(def, 'caster', onHit || !onCrit ? 'always' : 'crit', flag(def, 'triggers_if_roll_type_is_at_adv'));
      if (why) return no(why, def);
    }
    if (triggered && rt === EXTRA_WEAPON_DAMAGE_ROLL && triggerListed(def)) {
      const onHit = flag(def, 'triggers_on_hit'), onCrit = flag(def, 'triggers_on_critical_hit');
      if (onHit || onCrit) {
        const why = planFragment(def, 'caster', onHit ? 'always' : 'crit', flag(def, 'triggers_if_roll_type_is_at_adv'));
        if (why) return no(why, def);
      }
    }
    if (triggered) return null;
    if (rt === EXTRA_DAMAGE_ROLL && extraTypeGate(def)) { const why = planFragment(def, 'caster', 'always', false); if (why) return no(why, def); }
    const why = rollOneExtraWeapon(def, 'caster');
    return why ? no(why, def) : null;
  };
  // 1. the attacker's passive bucket (bare-string entries are skipped by PHP's `!is_array` guard)
  for (const raw of read.entries(attacker, 'passive_ability')) {
    const entry = record(raw);
    if (!entry || !entryActive(entry)) continue;
    const def = resolveEntryDef(attacker, entry);
    if (def === 'none') continue;
    if (!def) return no('extra-damage-definition-unavailable');
    const failed = casterPassive(def);
    if (failed) return failed;
  }
  // 1b. the passive ITEMS applied to the attacker as statuses, after the bucket (ft_status_item_passive_entries — dedup, team, conditions)
  const statusPassives = statusItemPassiveEntries(attacker, read.statusRows(attacker).map(status => {
    const embedded = record(status.status_arr);
    return { status, definition: hasLayers(embedded) ? embedded : read.definitionOn(attacker, upper(scalar(status.status_item_id) ?? itemField(embedded, 'item_id'))) };
  }), { value: read.value, entries: read.entries, definition: id => read.definitionOn(attacker, id), member: id => read.member?.(id),
    conditionScene: read.conditionScene,
    relevant: def => [EXTRA_DAMAGE_ROLL, EXTRA_WEAPON_DAMAGE_ROLL].includes(upper(scalar(read.value(def, 'roll_type')))) });
  if ('gap' in statusPassives) return no(statusPassives.gap);
  for (const { definition } of statusPassives.entries) {
    checkedStatusPassives.add(definition);
    const failed = casterPassive(definition); if (failed) return failed;
  }
  // 2. the weapon's own attached passive / ability entries (hydrated {item_arr} rows or bare UUIDs; lean {item_id} rows are skipped)
  for (const raw of [...read.entries(weapon, 'passive_ability'), ...read.entries(weapon, 'ability')]) {
    const entry = record(raw);
    let def: Row | null = null;
    // A hydrated row is used AS-IS — PHP never resolves a shell here, so a layerless shell simply rolls nothing.
    if (entry && record(entry.item_arr)) { if (!hasLayers(record(entry.item_arr))) continue; def = record(entry.item_arr); }
    else if (typeof raw === 'string' && UUID.test(raw)) def = read.definitionOn(attacker, upper(raw));
    else continue;
    if (!def) return no('weapon-extra-damage-definition-unavailable');
    if (read.entries(def, MAX_DAMAGE_VS_CREATURE_TYPE_LAYER).length > 0) return no('max-damage-vs-creature-type-out-of-scope');
    const why = rollOneExtraWeapon(def, 'weapon');
    if (why) return no(why, def);
  }
  // 3. the target's extra-damage-taken passives (one roll per ability + granting caster)
  const statusCasterById = new Map<string, string>();
  for (const status of read.statusRows(target)) statusCasterById.set(upper(status.status_id), String(status.status_caster_id ?? ''));
  const takenKeys = new Set<string>();
  for (const raw of read.entries(target, 'passive_ability')) {
    const entry = record(raw);
    if (!entry || !entryActive(entry)) continue;
    const restored = !('item_arr' in entry) && typeof entry.item_id === 'string' && UUID.test(entry.item_id) ? { ...entry, item_arr: entry.item_id } : entry;
    if (restored.item_arr === undefined || restored.item_arr === null) continue;
    const def = resolveEntryDef(target, restored);
    if (def === 'none') continue;
    if (!def) return no('target-extra-damage-definition-unavailable');
    if (containsTriggerKey(def)) continue;
    // layer_has: ANY stored value (a 0 included) arms the gate.
    const markGate = read.value(def, 'target_marked_by_caster');
    if (read.entries(def, 'target_marked_by_caster').length > 0 || (markGate !== undefined && markGate !== null)) {
      const source = String(entry.source ?? '');
      const marked = read.statusRows(target).some(s => String(s.status_id ?? '') === source && String(s.status_caster_id ?? '') === input.attackerId);
      if (!marked) continue;
    }
    if (upper(scalar(read.value(def, 'roll_type'))) !== EXTRA_DAMAGE_ROLL_TAKEN) continue;
    const abilityId = upper(typeof entry.ability_id === 'string' && entry.ability_id ? entry.ability_id : typeof restored.item_arr === 'string' ? restored.item_arr : itemField(def, 'item_id'));
    if (UUID.test(abilityId)) {
      const granter = upper(statusCasterById.get(upper(entry.source)) ?? '');
      const key = abilityId + '|' + granter;
      if (takenKeys.has(key)) continue;
      takenKeys.add(key);
    }
    const why = planFragment(def, 'target', 'always', false);
    if (why) return no(why, def);
    // the key a paired-limb rider skips once the paid hit rolled it (weapon-attack.ts riderPlans — PHP's $edt_rolled_per_action)
    const planned = plans[plans.length - 1];
    if (planned && UUID.test(abilityId)) planned.grantKey = abilityId + '|' + upper(statusCasterById.get(upper(entry.source)) ?? '');
  }

  // ---- the untyped adjusters calc_total applies after the fragments ----
  const percent = (row: Row, name: string): number | null => {
    const raw = scalar(read.value(row, name));
    if (raw === undefined || raw === null || raw === '' || raw === 0 || raw === '0') return 0;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  };
  const outputPercent = percent(attacker, 'all_damage_output_changed_by_percentage');
  const incomingPercent = percent(target, 'all_incoming_damage_changed_by_percentage');
  if (outputPercent === null || incomingPercent === null) return no('damage-percentage-unreadable');
  const mitigationRaw = scalar(read.value(target, 'damage_mitigation'));
  const damageMitigation = mitigationRaw === undefined || mitigationRaw === null || mitigationRaw === '' ? 0 : Math.trunc(Number.parseInt(String(mitigationRaw), 10));
  if (!Number.isFinite(damageMitigation)) return no('damage-mitigation-unreadable');
  const skippedEffects = new Set<string>();
  const providers = damageMitigation > 0 ? damageProviderDefinitions(read, attacker, weapon,
    new Set(['damage_ignores_all_damage_mitigation', 'attacks_ignore_damage_mitigation',
      ATTACKS_IGNORE_NATURAL_DAMAGE_MITIGATION, 'ignore_damage_mitigation_damage_type']),
    () => skippedEffects.add(`mitigation-ignore:status-definition-not-held (${String(ecLean(attacker, 'encounter_creature_name')
      ?? itemField(attacker, 'item_name') ?? 'Creature')})`)) : [];
  if (typeof providers === 'string') return no(providers);
  const providerMitigationIgnored = damageProviderFlag(read, providers, 'damage_ignores_all_damage_mitigation')
    || damageProviderFlag(read, providers, 'attacks_ignore_damage_mitigation');
  const mitigationIgnoredTypes = ignoredMitigationTypes(read, providers);
  // attacks_ignore_natural_damage_mitigation (Psion Metamorph "Living Arsenal", 2026-10-07 — PHP ft_attacks_ignore_natural_damage_mitigation
  // / ft_natural_damage_mitigation, R-180): this weapon HIT skips the target's natural Damage Mitigation (its base creature definition's DM).
  // Settled on state, before any draw — the base definition must be held (attack-truth prefetches it).
  let naturalMitigation = 0;
  if (damageMitigation > 0 && !providerMitigationIgnored) {
    if (damageProviderFlag(read, providers, ATTACKS_IGNORE_NATURAL_DAMAGE_MITIGATION)) {
      const natural = naturalDamageMitigation(target, damageMitigation, (id) => read.definitionOn(target, id), read.value);
      if (natural === null) return no('natural-mitigation-base-definition-not-held');
      naturalMitigation = natural;
    }
  }
  return { supported: true, plans, outputPercent, incomingPercent, damageMitigation: damageMitigation > 0 ? damageMitigation : 0,
    mitigationIgnoredTypes, naturalMitigation, reductions, ...(providerMitigationIgnored ? { providerMitigationIgnored } : {}),
    ...(skippedEffects.size ? { skippedEffects: [...skippedEffects] } : {}) };
}

/** Apply mitigation steps in PHP's order, tracking feedback_zero_reason; the fragment floors at 0. */
export function mitigateFragment(value: number, steps: readonly FragmentMitigationStep[]): { result: number; zeroReason: string | null } {
  let v = value; let reason: string | null = null;
  for (const step of steps) {
    if (step.ignored) continue; // the attacker's ignore_damage_type_resistance — logged (ρ), not applied
    if (step.kind === 'resistance') { v = Math.floor(v / 2); if (v === 0 && !reason) reason = 'fully resisted'; }
    else if (step.kind === 'immunity') { v = 0; reason = 'immune'; }
    else v *= 2;
  }
  return { result: Math.max(0, Math.trunc(v)), zeroReason: reason };
}

/** Roll one planned extra fragment (damage_type_roll for a passive source): dice → explosions → crit → mitigation. */
export function rollExtraFragment(roll: RollSource, plan: ExtraDamagePlan, context: { critical: boolean; variant: CritVariant; canExplode: boolean; fortune: number; multiplier?: number }): ExtraDamageFragment {
  const draws: number[] = [];
  for (let i = 0; i < plan.dice; i += 1) draws.push(roll.d(plan.die));
  // damage_type_is_rolled_at_max: the dice still draw (the tape stays in step), the fragment reads dice × die, and a forced extreme never explodes
  const maxed = plan.maxedBy != null;
  const canExplode = context.canExplode && !maxed;
  const explosions: ExtraDamageFragment['explosions'] = [];
  if (canExplode) for (const face of draws) explosions.push(...explosionChain(roll, face, plan.die, context.fortune));
  let value = (maxed ? plan.dice * plan.die : draws.reduce((sum, face) => sum + face, 0)) + plan.mod + explosions.reduce((sum, link) => sum + link.value, 0);
  let criticalVariant: CritVariant | null = null; let criticalBonus = 0; let criticalTuples: ExtraDamageFragment['criticalTuples'] = [];
  if (context.critical) {
    const bonus = criticalHitBonus(roll, context.variant, { dice: plan.dice, die: plan.die, rollResult: value },
      { canExplode, fortune: context.fortune, multiplier: context.multiplier });
    criticalVariant = bonus.variant; criticalBonus = bonus.total;
    criticalTuples = bonus.tuples.map(t => ({ value: t.value, label: t.label, kind: t.kind }));
    value += bonus.total;
  }
  const mitigated = mitigateFragment(value, plan.mitigation);
  // a failing post-condition gate zeroes the fragment AFTER mitigation; feedback_zero_reason stays mitigation's (PHP sets none for a gate)
  const result = plan.gates?.length ? 0 : mitigated.result;
  return { ...plan, draws, explosions, criticalVariant, criticalBonus, criticalTuples, beforeMitigation: value, result, zeroReason: mitigated.zeroReason };
}

/**
 * Sum the fragments, then apply output %, incoming %, EC-tier DR, Damage Mitigation and status reductions, flooring at 0.
 * Returns the total, the adjustment tuples in log order, and roll_feedback.
 */
export function totalDamage(
  fragments: ReadonlyArray<{ damageTypeId: string; beforeMitigation: number; result: number; zeroReason: string | null; healsCaster?: boolean }>,
  scope: { outputPercent: number; incomingPercent: number; damageMitigation: number; mitigationIgnoredTypes?: readonly string[]; naturalMitigation?: number;
    /** Only a non-critical hit below full AC takes the target's DR. */
    ecDamageReduction?: number;
    /** attacks_ignore_damage_mitigation / damage_ignores_all_damage_mitigation (the attacker or the weapon): the whole DM skipped, logged. */
    mitigationIgnored?: boolean; providerMitigationIgnored?: boolean; reductions?: DamageReduction[] },
  roll?: RollSource,
): { total: number; casterHealing: number; adjustments: DamageAdjustment[]; feedback: DamageFeedback } {
  let total = 0; let rolled = 0; let casterHealing = 0;
  const reasons: string[] = [];
  const byType = new Map<string, { id: string; rolled: number; applied: number; reasons: string[] }>();
  for (const f of fragments) {
    total += f.result;
    if (f.healsCaster) casterHealing += Math.max(0, f.result);
    const fr = Math.max(0, Math.trunc(f.beforeMitigation));
    rolled += fr;
    if (fr > 0) reasons.push(f.result === 0 ? (f.zeroReason ?? '') : '');
    const key = f.damageTypeId.trim().toUpperCase();
    if (key !== '') {
      const t = byType.get(key) ?? { id: f.damageTypeId.trim(), rolled: 0, applied: 0, reasons: [] };
      t.rolled += fr; t.applied += Math.max(0, f.result);
      if (fr > 0) t.reasons.push(f.result === 0 ? (f.zeroReason ?? '') : '');
      byType.set(key, t);
    }
  }
  const adjustments: DamageAdjustment[] = [];
  const percentage = (pct: number, key: string, source: string): void => {
    if (pct === 0) return;
    const adjusted = applyDamagePercentage(total, pct);
    const change = adjusted.change;
    total = adjusted.total;
    if (change === 0) return;
    const words = key.split('_').map((w, i) => (i > 0 && ['a', 'an', 'the', 'and', 'but', 'or', 'for', 'nor', 'of', 'on', 'at', 'to', 'from', 'by', 'in', 'with'].includes(w)) ? w : w.charAt(0).toUpperCase() + w.slice(1));
    adjustments.push({ face: 'Δ' + change, label: words.join(' ') + (source ? ' — ' + source : '') });
  };
  percentage(scope.outputPercent, 'all_damage_output_changed_by_percentage', 'Caster Bonus');
  percentage(scope.incomingPercent, 'all_incoming_damage_changed_by_percentage', '');
  if (scope.ecDamageReduction) {
    total = Math.max(0, total - scope.ecDamageReduction);
    casterHealing = Math.max(0, casterHealing - scope.ecDamageReduction);
    adjustments.push({ face: '-' + scope.ecDamageReduction, label: 'Damage Reduction' });
  }
  // calc_total: $attack_ignores_damage_mitigation (attacks_ignore_damage_mitigation on the attacker / weapon, OR'd with
  // damage_ignores_all_damage_mitigation — Perfect Breach, 2026-10-08 follow-up) skips the whole Damage Mitigation, logged "Damage Mitigation Ignored"
  if (scope.damageMitigation > 0 && (scope.mitigationIgnored || scope.providerMitigationIgnored)) adjustments.push({ face: '0', label: 'Damage Mitigation Ignored' });
  else if (scope.damageMitigation > 0) {
    // ignore_damage_mitigation_damage_type (Molten Impact, 2026-10-07 — R-147): exempt-type fragments are not reduced
    const ignored = new Set((scope.mitigationIgnoredTypes ?? []).map((id) => id.trim().toUpperCase()));
    const exempt = ignored.size ? fragments.reduce((sum, f) => sum + (ignored.has(f.damageTypeId.trim().toUpperCase()) ? Math.max(0, f.result) : 0), 0) : 0;
    // …from what is left once attacks_ignore_natural_damage_mitigation (Living Arsenal — R-180) skipped the target's natural share
    const subtracted = mitigationAfterTypedIgnore(mitigationAfterNaturalIgnore(scope.damageMitigation, scope.naturalMitigation ?? 0), total, exempt);
    if (subtracted === 0) adjustments.push({ face: '0', label: 'Damage Mitigation Ignored' });
    else {
      total = Math.max(0, total - subtracted);
      casterHealing = Math.max(0, casterHealing - subtracted);
      adjustments.push({ face: '-' + subtracted, label: 'Damage Mitigation' });
    }
  }
  if (scope.reductions?.length) {
    if (!roll) throw new Error('reduce-damage:roll-source-not-held');
    const reduced = rollDamageReductions(total, scope.reductions, roll);
    casterHealing = Math.max(0, casterHealing - (total - reduced.total));
    total = reduced.total;
    adjustments.push(...reduced.adjustments);
  }
  total = Math.max(0, total);
  const unique = (list: string[]): string[] => [...new Set(list)];
  const typeRows = [...byType.values()];
  const exact = typeRows.reduce((sum, t) => sum + t.applied, 0) === total;
  const feedback: DamageFeedback = {
    rolled, applied: total, verb: 'taken',
    reason: total === 0 && unique(reasons).length === 1 ? (reasons[0] || null) : null,
  };
  if (typeRows.length > 0) {
    feedback.damage_types = typeRows.map(t => ({
      damage_type_id: t.id, rolled: t.rolled, applied: exact ? t.applied : null, typed: t.applied,
      reason: t.applied === 0 && unique(t.reasons).length === 1 ? (t.reasons[0] || null) : null,
    }));
  }
  return { total, casterHealing, adjustments, feedback };
}
